import { useState } from "react";
import type { MatchSummary } from "../game/types";
import { Crest } from "../ui/atoms";
import { CartaoCompartilhavel } from "../ui/CartaoCompartilhavel";
import type { DadosDeCartao } from "../ui/cartoes";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { fatorDaPartida, lanceDaPartida, rotuloDeFase, useT, type Chave } from "../i18n";

const RESULT_COLOR: Record<string, string> = {
  V: "var(--success)",
  E: "var(--warning)",
  D: "var(--danger)",
};

const RESULT_LABEL: Record<string, Chave> = {
  V: "partida.vitoria",
  E: "partida.empate",
  D: "partida.derrota",
};

/**
 * Resultado da rodada.
 *
 * Os três fatores vêm do motor e são a resposta à crítica mais antiga do
 * gênero: o jogador perde e não sabe por quê.
 */
export function MatchPanel({ match, onClose }: { match: MatchSummary; onClose: () => void }) {
  const t = useT();
  const [cartao, setCartao] = useState(false);

  useDialogoDeAtalhos({
    nome: "Resumo da partida",
    nivel: 20,
    aoFechar: onClose,
    atalhos: { enter: onClose, c: () => setCartao(true) },
  });

  /*
   * De que lado o clube do treinador jogou.
   *
   * O resumo só diz se ele venceu, empatou ou perdeu, então o placar responde
   * nos dois primeiros casos. No empate, o lance marcado como "meu" entrega o
   * lado; sem lance nenhum, o cartão fica sem o destaque, e só.
   */
  const meuLado: "mandante" | "visitante" | null =
    match.myResult === "V"
      ? match.homeGoals > match.awayGoals
        ? "mandante"
        : "visitante"
      : match.myResult === "D"
        ? match.homeGoals < match.awayGoals
          ? "mandante"
          : "visitante"
        : (() => {
            const meu = match.events.find((evento) => evento.mine);
            if (!meu) return null;
            return meu.clubShort === match.homeShort ? "mandante" : "visitante";
          })();

  const emPontosCorridos = match.stage === "liga" || match.stage === "estadual";
  const rotuloDoResultado = RESULT_LABEL[match.myResult];

  const dados: DadosDeCartao = {
    tipo: "resultado",
    meuLado,
    contexto: emPontosCorridos
      ? match.competitionName
      : `${match.competitionName} · ${match.stage}`,
    mandante: { nome: match.homeName, sigla: match.homeShort, cores: match.homeColors },
    visitante: { nome: match.awayName, sigla: match.awayShort, cores: match.awayColors },
    golsMandante: match.homeGoals,
    golsVisitante: match.awayGoals,
    resultado: match.myResult,
    posse: match.possession,
    chances: match.chances,
    chancesDoRival: match.opponentChances,
    fatores: match.factors.slice(0, 3).map((fator) => fatorDaPartida(fator.key, fator.params)),
    classico: match.derby,
  };

  return (
    <div
      role="dialog"
      aria-label={t("partida.aria")}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 20,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: "min(580px, 100%)", maxHeight: "88vh", overflow: "auto", padding: 20 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: "space-between", gap: 10 }}>
          <span className="label">
            {match.competitionName}
            {emPontosCorridos ? "" : t("partida.competicaoFase", { fase: rotuloDeFase(match.stage) })}
          </span>
          {match.derby && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--danger)",
                border: "1px solid rgba(229,72,77,0.5)",
                borderRadius: 4,
                padding: "2px 7px",
              }}
            >
              {t("partida.classico")}
            </span>
          )}
        </div>

        <div
          className="row"
          style={{ justifyContent: "space-between", margin: "16px 0 6px", gap: 14 }}
        >
          <div className="row" style={{ flex: 1, gap: 10, minWidth: 0 }}>
            <Crest colors={match.homeColors} short={match.homeShort} size={42} />
            <span style={{ fontWeight: 700, fontSize: 14 }}>{match.homeName}</span>
          </div>
          <span className="num" style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em" }}>
            {match.homeGoals} <span style={{ color: "var(--text-dim)" }}>x</span> {match.awayGoals}
          </span>
          <div
            className="row"
            style={{ flex: 1, gap: 10, minWidth: 0, justifyContent: "flex-end" }}
          >
            <span style={{ fontWeight: 700, fontSize: 14, textAlign: "right" }}>{match.awayName}</span>
            <Crest colors={match.awayColors} short={match.awayShort} size={42} />
          </div>
        </div>

        <p style={{ margin: "0 0 16px", color: RESULT_COLOR[match.myResult], fontWeight: 800 }}>
          {rotuloDoResultado ? t(rotuloDoResultado) : ""}
        </p>

        <div className="row" style={{ gap: 20, marginBottom: 16 }}>
          <span className="muted num">{t("partida.posse", { valor: match.possession })}</span>
          <span className="muted num">
            {t("partida.chances", { minhas: match.chances, dele: match.opponentChances })}
          </span>
        </div>

        {match.factors.length > 0 && (
          <section style={{ marginBottom: 16 }}>
            <h3 className="label" style={{ marginBottom: 7 }}>
              {t("partida.porQueAconteceu")}
            </h3>
            <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
              {match.factors.slice(0, 3).map((factor, index) => (
                <li key={index} style={{ marginBottom: 4 }}>
                  {fatorDaPartida(factor.key, factor.params)}
                </li>
              ))}
            </ul>
          </section>
        )}

        {match.events.length > 0 && (
          <section>
            <h3 className="label" style={{ marginBottom: 7 }}>
              {t("partida.lances")}
            </h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {match.events.map((event, index) => (
                <li
                  key={index}
                  className="row"
                  style={{ gap: 9, padding: "4px 0", opacity: event.mine ? 1 : 0.72 }}
                >
                  <span className="num" style={{ minWidth: 28, color: "var(--text-dim)" }}>
                    {t("partida.minuto", { minuto: event.minute })}
                  </span>
                  <span
                    style={{
                      minWidth: 36,
                      fontSize: 10,
                      fontWeight: 800,
                      textAlign: "center",
                      borderRadius: 4,
                      padding: "2px 5px",
                      background: event.mine ? "var(--primary)" : "var(--surface-3)",
                      color: event.mine ? "var(--primary-ink)" : "var(--text-muted)",
                    }}
                  >
                    {event.clubShort}
                  </span>
                  <span
                    style={{
                      color: event.kind === "gol" ? "var(--accent)" : "inherit",
                      fontWeight: event.kind === "gol" ? 600 : 400,
                    }}
                  >
                    {lanceDaPartida(event.key, event.params)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="row" style={{ gap: 8, marginTop: 20 }}>
          <button className="ghost" onClick={() => setCartao(true)}>
            {t("partida.compartilhar")}
          </button>
          <button className="primary" style={{ flex: 1 }} onClick={onClose}>
            {t("comum.continuar")}
          </button>
        </div>
      </div>

      {cartao && <CartaoCompartilhavel dados={dados} aoFechar={() => setCartao(false)} />}
    </div>
  );
}
